import "./GalleryPage.style.css";

import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { GalleryTable } from "./GalleryTable/GalleryTable";
import { GameCardState } from "../../utils/states/ConstructorPageStates/GameCardState";
import { ApiFactory } from "../../api-client/api-factory";
import { GameCardDto, GamesApi, TagsApi } from "../../api-client";
import { GameCardMapper } from "../../utils/mappers/GameCardMapper";
import { SortingType } from "../../utils/enums/SortingType";
import { GALLERY_GAMES_CHUNK_SIZE } from "../../utils/constants/constants";
import { UniqueId } from "../../utils/UniqueId";
import { getOrdering } from "../../utils/functions/getOrdering";
import { Switcher } from "../../components/base/Switcher/Switcher";
import {
  Button,
  ButtonColor,
  ButtonSize,
} from "../../components/base/Button/Button";
import { Input } from "../../components/base/Input/Input";

import { IconFilter24 } from "../../components/icons/IconFilter/IconFilter24";
import { useIsMobile } from "../../utils/functions/useIsMobile";
import { GalleryFiltersMenu } from "./GalleryFiltersMenu/GalleryFiltersMenu";
import { CSSTransition } from "react-transition-group";

export const GalleryPage = () => {
  const [isGamesReceived, setIsGamesReceived] = useState<boolean>(false);
  const [gameCards, setGameCards] = useState<GameCardState[]>([]);
  const [tags, setTags] = useState<[string, number][]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pagesCount, setPagesCount] = useState<number>(1);

  const [searchingWords, setSearchingWords] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedFavorites, setSelectedFavorites] = useState<boolean>(false);
  const [selectedOwn, setSelectedOwn] = useState<boolean>(false);
  const [sortingType, setSortingType] = useState<SortingType>(
    SortingType.Popular,
  );

  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);
  const [showFiltersMenu, setShowFiltersMenu] = useState<boolean>(false);

  const filtersMenuRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isGamesReceived) {
      UniqueId.clear();

      ApiFactory.get(TagsApi)
        .tagsGet()
        .then((data) => {
          setTags(data.map((tag) => [tag.name!, tag.count!]));
        })
        .catch(() => console.error("ERR: Не получилось получить тэги игр"));

      ApiFactory.get(GamesApi)
        .gamesGet(GALLERY_GAMES_CHUNK_SIZE, 1)
        .then((data) => {
          setGameCards(
            GameCardMapper.toGameCardStates(data.games as GameCardDto[]),
          );
          setIsGamesReceived(true);
        })
        .catch(() =>
          console.error("ERR: Не получилось получить данные об играх"),
        );
    }
  }, [isGamesReceived]);

  useEffect(() => {
    ApiFactory.get(GamesApi)
      .gamesGet(
        GALLERY_GAMES_CHUNK_SIZE,
        currentPage,
        pagesCount,
        selectedTags,
        searchingWords.split(" ").filter((word) => word.trim() !== ""),
        selectedOwn,
        selectedFavorites,
        getOrdering(sortingType),
      )
      .then((data) => {
        setCurrentPage(data.paginationInfo?.pageNumber!);
        setPagesCount(data.paginationInfo?.pagesCount!);
        setGameCards(
          GameCardMapper.toGameCardStates(data.games as GameCardDto[]),
        );
      })
      .catch(() =>
        console.error("ERR: Не получилось получить данные об играх"),
      );
  }, [
    selectedTags,
    sortingType,
    searchingWords,
    selectedFavorites,
    selectedOwn,
    currentPage,
    pagesCount,
  ]);

  const handleClickTagFilter = (tagName: string) => {
    const isSelected = selectedTags.indexOf(tagName) === -1;

    if (isSelected) {
      setSelectedTags((prevState) => [...prevState, tagName]);
    } else {
      setSelectedTags((prevState) =>
        prevState.filter((selectedTag) => selectedTag !== tagName),
      );
    }
  };

  const handleClickOwnFilter = () => {
    setSelectedOwn((prevState) => !prevState);
  };

  const handleClickFavoritesFilter = () => {
    setSelectedFavorites((prevState) => !prevState);
  };

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchingWords(e.target.value);
  };

  const handleNavigatePopular = () => {
    setIsSearchMode(false);
    setShowFiltersMenu(false);
    setSearchingWords("");
    setSelectedTags([]);
    setSelectedFavorites(false);
    setSelectedOwn(false);
    setSortingType(SortingType.Popular);
  };

  const handleNavigateNew = () => {
    setIsSearchMode(false);
    setShowFiltersMenu(false);
    setSearchingWords("");
    setSelectedTags([]);
    setSelectedFavorites(false);
    setSelectedOwn(false);
    setSortingType(SortingType.PublicationDate);
  };

  const handleNavigateSearch = () => {
    setIsSearchMode(true);
  };

  const handleClickFiltersButton = () => {
    setShowFiltersMenu(true);
  };

  return (
    <div className="gallery-page">
      {!isMobile && (
        <Switcher
          className={isSearchMode ? "" : "gallery-page__nav_no-search"}
          color={ButtonColor.Shiny}
          size={ButtonSize.Large}
          items={[
            { content: "Популярные", onActive: handleNavigatePopular },
            { content: "Новинки", onActive: handleNavigateNew },
            // { content: "Недавно играли" }, - TODO: Bring this back with new frontend functionality
            { content: "Поиск", onActive: handleNavigateSearch },
          ]}
          startingItem={0}
        />
      )}
      {(isSearchMode || isMobile) && (
        <div className="gallery-page__search">
          <Input
            className="gallery-page__search-input"
            name="gallery-search"
            placeholder={
              isMobile
                ? "Поиск по играм и авторам"
                : "Поиск по названию, автору и тегам"
            }
            onChange={handleSearchInputChange}
          />
          <Button
            icon={<IconFilter24 />}
            size={ButtonSize.Small}
            onClick={handleClickFiltersButton}
          />
          <CSSTransition
            in={showFiltersMenu}
            timeout={300}
            classNames={"gallery-page__filters-menu_animated"}
            unmountOnExit
            nodeRef={filtersMenuRef}
          >
            <GalleryFiltersMenu
              className="gallery-page__filters-menu"
              ref={filtersMenuRef}
              tags={tags}
              selectedTags={selectedTags}
              sortingType={sortingType}
              filterOwn={selectedOwn}
              filterFavorites={selectedFavorites}
              handleChangeSortingType={setSortingType}
              handleToggleOwnFilter={handleClickOwnFilter}
              handleToggleFavoritesFilter={handleClickFavoritesFilter}
              handleToggleTagFilter={handleClickTagFilter}
              handleClickCloseButton={() => setShowFiltersMenu(false)}
            />
          </CSSTransition>
        </div>
      )}
      <GalleryTable gameCards={gameCards} isGamesReceived={isGamesReceived} />
    </div>
  );
};
